import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function useHSCodeList() {
  // Use toast
  const toast = useToast();

  const refHSCodeListTable = ref(null);

  // Loading state
  const isTableLoading = ref(false);

  // Table Handlers
  const tableColumns = [
    { key: 'id', label: 'ID', sortable: false, thStyle: { minWidth: '60px' } },
    { key: 'code', sortable: false, stickyColumn: true },
    { key: 'name', sortable: false, thStyle: { minWidth: '200px' } },
    { key: 'description', sortable: false, thStyle: { minWidth: '350px' } },
    { key: 'isActive', label: 'Status', sortable: false, thStyle: { minWidth: '100px' } },
    { key: 'createdBy', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'updatedBy', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'actions', sortable: false, stickyColumn: true, thStyle: { minWidth: '120px' } },
  ];
  const perPage = ref(10);
  const totalHSCodes = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');

  // Filter states
  const isActiveFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refHSCodeListTable.value
      ? refHSCodeListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalHSCodes.value,
    };
  });

  const refetchData = () => {
    refHSCodeListTable.value.refresh();
  };

  // Debounced search function
  const debouncedSearch = debounce(() => {
    refetchData();
  }, 500); // 500ms delay

  // Watch for page and perPage changes (immediate)
  watch([currentPage, perPage], () => {
    refetchData();
  });

  // Watch for search query changes (debounced)
  watch(searchQuery, () => {
    debouncedSearch();
  });

  // Watch for filter changes
  watch(isActiveFilter, (newValue, oldValue) => {
    console.log('=== useHSCodeList isActiveFilter changed:', { oldValue, newValue });
    refetchData();
  });

  const fetchHSCodes = (ctx, callback) => {
    console.log('=== useHSCodeList fetchHSCodes()');

    isTableLoading.value = true;

    const params = {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      isActive: isActiveFilter.value,
    };

    console.log('=== useHSCodeList fetchHSCodes() params:', params);

    store
      .dispatch('fbr-invoice-hs-code/fetchHSCodes', params)
      .then((response) => {
        console.log('=== useHSCodeList fetchHSCodes() response', response);

        const { items, pagination } = response;

        callback(items);
        totalHSCodes.value = pagination.totalCount;
      })
      .catch((error) => {
        console.log('=== useHSCodeList fetchHSCodes() error', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching HS Codes list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      })
      .finally(() => {
        isTableLoading.value = false;
      });
  };

  return {
    fetchHSCodes,
    tableColumns,
    perPage,
    currentPage,
    totalHSCodes,
    dataMeta,
    perPageOptions,
    searchQuery,
    refHSCodeListTable,

    // Loading state
    isTableLoading,

    refetchData,

    // Filter states
    isActiveFilter,
  };
}
